// src/frontend/.../Recruitment/Sections/Section3.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "../../../../../css/Recruitment/Sections.css";
import YesNoRadio from "../RadioButton";
import RadioButtonGrid from "../RadioButtonGrid";
import "react-datepicker/dist/react-datepicker.css";
import UseDatepicker from "../Datepicker";
import MobileNumberInput from "../../../../DesignSystem/Inputs/MobileNumberInput";

import writeDataS3 from "../../../../../backend/Recruitment/Sections/Section3/writeDataS3";
import readDataS3 from "../../../../../backend/Recruitment/Sections/Section3/readDataS3";
import { formatDateValue } from "../Utils/dateFormatting";

import {
  addSubmissionListener,
  getIsSubmitted,
} from "../../Utils/submissionListener";

import errorTrackerV2 from "../Validation/Utils/ErrorTrackerV2";
import { auth } from "../../../../../backend/firebaseConfig";
import { validateSection3 } from "../Validation/section3";
import DateField from "../../../../DesignSystem/Inputs/DateField";
import InputField from "../../../../DesignSystem/Inputs/InputField";
import AlertBanner from "../../../../DesignSystem/AlertBanner/AlertBanner";
import PrimaryButton from "../../../../DesignSystem/Buttons/PrimaryButton";
import TextArea from "../../../../DesignSystem/Inputs/TextArea";

// ---------- helpers ----------
const makeEmptyWorkRow = (toDate = "") => ({
  nameOfFirm: "",
  natureOfBusiness: "",
  addressOfFirm: "",
  firmTown: "",
  firmCounty: "",
  firmPostcode: "",
  officeTelephone: "",
  emailAddress: "",
  from: "",
  to: toDate, // keep Date or "" (never string dates)
  currentIncome: "",
  FCARegulated: null,
  currentJobTitle: "",
  regulatedRole: null,
  appointedRepresentative: null,
  lifeAssuranceCompany: "",
  authorisationCommenced: "",
  currentRole: "",
  currentEmploymentStatus: "",
  educationDetails: "",
  reasonsForLeaving: "",
  noticePeriod: "",
  intendedResignation: "",
  contactForReference: null,
  reasonForNoReference: "",
  competentAdviser: null,
});

const makeEmptyLifeCompanyRow = () => ({ date: "", company: "" });

const flattenWorkHistoryErrors = (workHistoryErrors = []) => {
  const flat = {};
  workHistoryErrors.forEach((rowErrors = {}, i) => {
    Object.entries(rowErrors).forEach(([field, msg]) => {
      if (msg) flat[`workHistory_${i}_${field}`] = msg;
    });
  });
  return flat;
};

const safeDate = (maybe) => {
  if (!maybe) return "";
  if (maybe instanceof Date) return maybe;
  // Firestore Timestamp-like
  if (typeof maybe === "object" && maybe?.seconds) {
    return new Date(maybe.seconds * 1000);
  }
  const d = new Date(maybe);
  return isNaN(d.getTime()) ? "" : d;
};

const isValidDate = (d) => d instanceof Date && !isNaN(d.getTime());

const sameDay = (a, b) => {
  if (!isValidDate(a) || !isValidDate(b)) return false;
  return a.toDateString() === b.toDateString();
};

const buildTenYearsAgo = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 10);
  d.setHours(0, 0, 0, 0);
  return d;
};

const workRowsEqual = (a = [], b = []) => {
  if (a.length !== b.length) return false;

  for (let i = 0; i < a.length; i++) {
    const A = a[i] || {};
    const B = b[i] || {};

    // Only compare the fields that matter for auto logic
    const aFrom = isValidDate(A.from) ? A.from : "";
    const bFrom = isValidDate(B.from) ? B.from : "";
    const aTo = isValidDate(A.to) ? A.to : "";
    const bTo = isValidDate(B.to) ? B.to : "";

    if (aFrom === "" && bFrom !== "") return false;
    if (aFrom !== "" && bFrom === "") return false;
    if (aFrom !== "" && !sameDay(aFrom, bFrom)) return false;

    if (aTo === "" && bTo !== "") return false;
    if (aTo !== "" && bTo === "") return false;
    if (aTo !== "" && !sameDay(aTo, bTo)) return false;
  }

  return true;
};

const isWorkRowBlank = (r) => {
  const row = r || {};
  return (
    !isValidDate(row.from) &&
    !String(row.nameOfFirm || "").trim() &&
    !String(row.currentEmploymentStatus || "").trim() &&
    !String(row.educationDetails || "").trim()
  );
};

const Section3 = ({ onValidationStatusChange }) => {
  // console.log(">>> Section 3 Component MOUNTED");

  const onValidationStatusChangeRef = useRef(onValidationStatusChange);
  const sectionTopRef = useRef(null);

  const [formData, setFormData] = useState({
    workHistory: [makeEmptyWorkRow()],
    lifeAssuranceCompanies: [makeEmptyLifeCompanyRow()],
  });

  const [errors, setErrors] = useState({
    workHistory: [
      /* row errs */
    ],
  });
  const [isFormValid, setIsFormValid] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("");
   const canDeleteWorkHistory = useMemo(() => {
     const rows = formData.workHistory || [];
     if (rows.length <= 1) return false; // keep at least 1 row always

     // allow delete ONLY if we won't break coverage
     // i.e. after deleting last row, the new last.from is still <= 10 years ago OR last row is blank
     const tenYearsAgo = buildTenYearsAgo();

     const last = rows[rows.length - 1];
     const secondToLast = rows[rows.length - 2];

     // if last row is blank, allow delete
     if (isWorkRowBlank(last)) return true;

     const secondFrom = isValidDate(secondToLast?.from)
       ? secondToLast.from
       : null;
     return !!(secondFrom && secondFrom <= tenYearsAgo);
   }, [formData.workHistory]);
  const [isSubmitted, setIsSubmitted] = useState(getIsSubmitted());

  const employmentStatus = useMemo(
    () => ["Employed", "Unemployed", "Self-employed", "In Education"],
    [],
  );

  const reasonsForLeaving = useMemo(
    () => [
      "Resignation",
      "Retirement",
      "Termination/Dismissal",
      "End of contract",
      "Redundancy",
      "N/A",
    ],
    [],
  );

  // --- submission listener (fixed deps) ---
  useEffect(() => {
    const removeListener = addSubmissionListener(setIsSubmitted);
    setIsSubmitted(getIsSubmitted());
    return () => removeListener();
  }, []);

  // keep latest callback without re-creating validateFields
  useEffect(() => {
    onValidationStatusChangeRef.current = onValidationStatusChange;
  }, [onValidationStatusChange]);

  // --- validation (single source of truth) ---
  const validateFields = useCallback(async (nextData) => {
    const result = await validateSection3(nextData); // { workHistory: [...], _isValid }
    setErrors({ workHistory: result.workHistory || [] });
    setIsFormValid(!!result._isValid);

    // store ONLY real errors so tabs don't stay red
    const flatErrors = flattenWorkHistoryErrors(result.workHistory || []);
    // console.log("Section 3 Validation Errors:", flatErrors);
    // console.log("Section 3 Error Count:", Object.keys(flatErrors).length);
    errorTrackerV2.setSectionErrors("section3", flatErrors);

    onValidationStatusChangeRef.current?.(!!result._isValid);
    return !!result._isValid;
  }, []);

  useEffect(() => {
    validateFields(formData);
  }, [formData, validateFields]);

  // --- fetch data (no double setFormData) ---
  useEffect(() => {
    // console.log(">>> Section 3 useEffect for data fetch TRIGGERED");
    let isMounted = true;

    const fetchData = async () => {
      // console.log(">>> fetchData function STARTED");
      try {
        // console.log("Calling readDataS3()...");
        const data = await readDataS3();
        // console.log("SUCCESS: Section 3: Data loaded from Firebase:", data);

        if (!isMounted) return;

        if (!data) {
          // console.log("Section 3: No data found, initializing empty form");
          const init = {
            workHistory: [makeEmptyWorkRow(new Date())],
            lifeAssuranceCompanies: [makeEmptyLifeCompanyRow()],
          };
          setFormData(init);
          return;
        }

        const rawWorkHistory = data.workHistory || {};
        const rawLife = data.lifeAssuranceCompanies || {};

        //  console.log("\n=== CONVERSION DEBUG - rawWorkHistory:", rawWorkHistory);
        // console.log(
        //   "Object.keys(rawWorkHistory):",
        //   Object.keys(rawWorkHistory),
        // );
        // console.log(
        //   "Object.values(rawWorkHistory):",
        //   Object.values(rawWorkHistory),
        // );
        // console.log(
        //   "Object.values length:",
        //   Object.values(rawWorkHistory).length,
        // );

        // Check if data exists but Object.values returns empty
        if (Object.keys(rawWorkHistory).length === 0 && rawWorkHistory[0]) {
          // console.error(
          //   "WARNING CRITICAL: Data exists at index 0 but Object.keys/values return empty!",
          // );
          // console.error("This is a Firestore non-enumerable property issue");
        }

        // Try direct index access if Object.values fails
        let workHistoryArray = Object.values(rawWorkHistory);

        if (workHistoryArray.length === 0) {
          // console.warn(
          //   "Object.values returned empty, trying direct index access...",
          // );
          workHistoryArray = [];
          for (let i = 0; i < 10; i++) {
            if (rawWorkHistory[i]) {
              // console.log(`Found data at index ${i}:`, rawWorkHistory[i]);
              workHistoryArray.push(rawWorkHistory[i]);
            }
          }
          // console.log(
          //   "Direct access found",
          //   workHistoryArray.length,
          //   "entries",
          // );
        }

        // console.log("Final workHistoryArray for conversion:", workHistoryArray);

        const formattedWorkHistory = workHistoryArray.map((work) => ({
          ...makeEmptyWorkRow(),
          nameOfFirm: work?.nameOfFirm || "",
          natureOfBusiness: work?.natureOfBusiness || "",
          addressOfFirm: work?.addressOfFirm || "",
          firmTown: work?.firmTown || "",
          firmCounty: work?.firmCounty || "",
          firmPostcode: work?.firmPostcode || "",
          officeTelephone: work?.officeTelephone || "",
          emailAddress: work?.emailAddress || "",
          from: safeDate(work?.from),
          to: safeDate(work?.to),
          currentIncome: work?.currentIncome || "",
          FCARegulated: work?.FCARegulated ?? null,
          regulatedRole: work?.regulatedRole ?? null,
          appointedRepresentative: work?.appointedRepresentative ?? null,
          lifeAssuranceCompany: work?.lifeAssuranceCompany || "",
          authorisationCommenced: safeDate(work?.authorisationCommenced),
          currentRole: work?.currentRole || "",
          currentEmploymentStatus: work?.currentEmploymentStatus || "",
          educationDetails: work?.educationDetails || "",
          reasonsForLeaving: work?.reasonsForLeaving || "",
          noticePeriod: work?.noticePeriod || "",
          currentJobTitle: work?.currentJobTitle || "",
          intendedResignation: safeDate(work?.intendedResignation),
          contactForReference: work?.contactForReference ?? null,
          reasonForNoReference: work?.reasonForNoReference || "",
          competentAdviser: work?.competentAdviser ?? null,
        }));

        const formattedLifeAssuranceCompanies = Object.values(rawLife).map(
          (company) => ({
            date: safeDate(company?.date),
            company: company?.company || "",
          }),
        );

        const newFormData = {
          workHistory:
            formattedWorkHistory.length > 0
              ? formattedWorkHistory
              : [makeEmptyWorkRow()],
          lifeAssuranceCompanies:
            formattedLifeAssuranceCompanies.length > 0
              ? formattedLifeAssuranceCompanies
              : [makeEmptyLifeCompanyRow()],
        };
        // console.log(
        //   "Section 3: Setting form data, validation will trigger automatically",
        // );
        setFormData(newFormData);
      } catch (e) {
        // keep silent like your original, but ensure the form still renders
        if (!isMounted) return;
        setFormData({
          workHistory: [makeEmptyWorkRow(new Date())],
          lifeAssuranceCompanies: [makeEmptyLifeCompanyRow()],
        });
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);

  // --- auto-sync: "to" = previous "from" + extend until 10 years covered ---
  useEffect(() => {
    const tenYearsAgo = buildTenYearsAgo();

    setFormData((prev) => {
      let rows = Array.isArray(prev.workHistory) ? [...prev.workHistory] : [];

      if (rows.length === 0) rows = [makeEmptyWorkRow("")];

      // 1) Cascade the "to" dates (row 0 "to" stays whatever you want — but your UI disables it anyway)
      // Your UI expects "to" = previous.from for i>0.
      rows = rows.map((row, i) => {
        if (i === 0) return { ...makeEmptyWorkRow(row?.to || ""), ...row };

        const desiredTo = rows[i - 1]?.from;
        return {
          ...makeEmptyWorkRow(""),
          ...row,
          to: isValidDate(desiredTo) ? new Date(desiredTo) : "",
        };
      });

      // 2) Auto-add ONE row if last.from exists AND still inside 10 years
      const last = rows[rows.length - 1];
      const lastFrom = isValidDate(last?.from) ? last.from : null;

      if (lastFrom && lastFrom > tenYearsAgo) {
        rows.push(makeEmptyWorkRow(new Date(lastFrom)));
      }

      // 3) Trim multiple trailing blank rows (keep max 1 trailing blank row)
      while (
        rows.length > 1 &&
        isWorkRowBlank(rows[rows.length - 1]) &&
        isWorkRowBlank(rows[rows.length - 2])
      ) {
        rows.pop();
      }

      // 4) If the last.from is <= 10 years ago, we don't need a trailing blank row
      const newLast = rows[rows.length - 1];
      const newLastFrom = isValidDate(newLast?.from) ? newLast.from : null;

      if (newLastFrom && newLastFrom <= tenYearsAgo) {
        while (rows.length > 1 && isWorkRowBlank(rows[rows.length - 1]))
          rows.pop();
      }

      if (workRowsEqual(prev.workHistory, rows)) return prev;
      return { ...prev, workHistory: rows };
    });
  }, [
    // Only re-run when from dates change (not on every keystroke in text inputs)
    JSON.stringify(
      (formData.workHistory || []).map((r) =>
        isValidDate(r?.from) ? r.from.toISOString().slice(0, 10) : "",
      ),
    ),
  ]);


   const deleteWorkHistory = () => {
     setFormData((prev) => {
       const rows = [...(prev.workHistory || [])];
       if (rows.length <= 1) return prev;
       rows.pop();
       const nextData = { ...prev, workHistory: rows };
       writeDataS3(nextData);
       return nextData;
     });
   };
  const handleLifeAssuranceChange = (index, e) => {
    setFormData((prev) => {
      const updated = [...(prev.lifeAssuranceCompanies || [])];
      const row = updated[index] || makeEmptyLifeCompanyRow();

      if (e instanceof Date || e === "" || e === null) {
        updated[index] = { ...row, date: e || "" };
        // console.log(
        //   "handleLifeAssuranceChange - Date set at index",
        //   index,
        //   ":",
        //   e,
        //   "Full row:",
        //   updated[index],
        // );
      } else if (e?.target) {
        const { value } = e.target;
        updated[index] = { ...row, company: value };
        // console.log(
        //   "handleLifeAssuranceChange - Company set at index",
        //   index,
        //   ":",
        //   value,
        //   "Full row:",
        //   updated[index],
        // );
      }
      // console.log("Life Assurance Companies state updated:", updated);
      return { ...prev, lifeAssuranceCompanies: updated };
    });
  };

   const handleChange = (index, e) => {
     const { name, value } = e.target;
     const fieldName = name.includes("-") ? name.split("-")[0] : name;

     setFormData((prev) => {
       const wh = [...(prev.workHistory || [])];
       wh[index] = { ...(wh[index] || makeEmptyWorkRow()), [fieldName]: value };
       return { ...prev, workHistory: wh };
     });
   };

  const handleDateChange = (index, name, value) => {
    const nextDate = value instanceof Date ? value : safeDate(value);

    setFormData((prev) => {
      const wh = [...(prev.workHistory || [])];
      wh[index] = { ...(wh[index] || makeEmptyWorkRow()), [name]: nextDate };
      return { ...prev, workHistory: wh };
    });
  };

  const formatWithCommas = (num) => {
    if (!num) return "";
    const numericValue = String(num).replace(/\D/g, "");
    return numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setShowErrors(true);
    sectionTopRef.current?.scrollIntoView({ behavior: "smooth" });
    const ok = validateFields(formData);
    // console.log("Section 3 Submit - Validation Result:", ok);
    // console.log("Section 3 Form Data:", formData);

    // Save data regardless of validation status
    writeDataS3(formData);

    // Show appropriate alert based on validation result
    if (ok ) {
      setAlertMessage("Data saved successfully!");
      setAlertType("success");
    } else {
      setAlertMessage(
        "Data saved with errors. Please review the fields marked with errors.",
      );
      setAlertType("alert");
    }
  };

  // ---------- UI ----------
  return (
    <form className="section-form" onSubmit={handleSubmit} ref={sectionTopRef}>
      {alertMessage && (
        <AlertBanner
          type={alertType}
          message={alertMessage}
          onClose={() => setAlertMessage("")}
        />
      )}
      <h3>Section 3 – Activity</h3>
      {/* <div className="Content-SemiBold">Activity history - last ten years</div> */}
      <div className="Content">
        Please provide details of your complete employment, self-employment
        (full-time and/or part-time), unemployment, and/or education for the
        last <strong>ten years</strong> from the date of your application.
        References will be requested from all past and present employers
        covering the most recent <strong>six years</strong>.
      </div>

      {(formData.workHistory || []).map((work, index) => {
        const isEducationOrUnemployed = ["Unemployed", "In Education"].includes(
          work.currentEmploymentStatus,
        );

        return (
          <React.Fragment key={`work-history-${index}`}>
            {" "}
            <div className="section2-container">
              <div className="Content">{`3.${index + 1}`} Work History</div>

              <div className="input-group-row">
                <div className="Content">
                  {index === 0
                    ? "Current Employment Status"
                    : "Employment Status"}{" "}
                  <span style={{ color: "var(--Error)" }}>*</span>
                </div>
                <RadioButtonGrid
                  options={employmentStatus}
                  columns={2}
                  name={`currentEmploymentStatus-${index}`}
                  value={work.currentEmploymentStatus}
                  onChange={(e) => handleChange(index, e)}
                  // error={errors.workHistory?.[index]?.currentEmploymentStatus}
                />
              </div>

              <div className="input-group-row">
                <div className="Content">
                  Dates of employment/Self-employment/Education
                  <span style={{ color: "var(--Error)" }}>*</span>
                </div>
                <div className="input-group">
                  <DateField
                    name={`from-${index}`}
                    label="From"
                    value={work.from}
                    onChange={(e) => {
                      const dateValue = e.target.value
                        ? new Date(e.target.value)
                        : "";
                      handleDateChange(index, "from", dateValue);
                    }}
                    error={showErrors && !!errors.workHistory?.[index]?.from}
                    helperText={
                      showErrors ? errors.workHistory?.[index]?.from : ""
                    }
                  />

                  <DateField
                    name={`to-${index}`}
                    label="To"
                    value={work.to}
                    disabled={true}
                    onChange={(e) => {
                      const dateValue = e.target.value
                        ? new Date(e.target.value)
                        : "";
                      handleDateChange(index, "to", dateValue);
                    }}
                    error={showErrors && !!errors.workHistory?.[index]?.to}
                    helperText={
                      showErrors ? errors.workHistory?.[index]?.to : ""
                    }
                  />
                </div>
              </div>

              {isEducationOrUnemployed && (
                <div className="input-group-row">
                  <div className="Content">
                    Please add further details about your education/activities
                    during this period
                    <span style={{ color: "var(--Error)" }}>*</span>
                  </div>
                  <InputField
                    name="educationDetails"
                    placeholder="Add details here"
                    value={work.educationDetails}
                    error={
                      showErrors &&
                      !!errors.workHistory?.[index]?.educationDetails
                    }
                    helperText={
                      showErrors
                        ? errors.workHistory?.[index]?.educationDetails
                        : ""
                    }
                    onChange={(e) => handleChange(index, e)}
                    multiline
                    rows={4}
                    required
                  />
                </div>
              )}

              {!isEducationOrUnemployed && (
                <>
                  <div className="input-group-row">
                    <div className="Content">
                      Name of Firm{" "}
                      <span style={{ color: "var(--Error)" }}>*</span>
                    </div>
                    <InputField
                      name="nameOfFirm"
                      placeholder="Firm Name"
                      value={work.nameOfFirm}
                      error={
                        showErrors && !!errors.workHistory?.[index]?.nameOfFirm
                      }
                      helperText={
                        showErrors
                          ? errors.workHistory?.[index]?.nameOfFirm
                          : ""
                      }
                      onChange={(e) => handleChange(index, e)}
                    />
                  </div>

                  <div className="input-group-row">
                    <div className="Content">
                      Nature of Business
                      <span style={{ color: "var(--Error)" }}>*</span>
                    </div>
                    <InputField
                      name="natureOfBusiness"
                      placeholder="Nature of Business"
                      value={work.natureOfBusiness}
                      error={
                        showErrors &&
                        !!errors.workHistory?.[index]?.natureOfBusiness
                      }
                      helperText={
                        showErrors
                          ? errors.workHistory?.[index]?.natureOfBusiness
                          : ""
                      }
                      onChange={(e) => handleChange(index, e)}
                    />
                  </div>

                  <div className="separator" />

                  <div className="form-row">
                    <div className="Content">Address of Firm</div>
                    <div className="input-group input-group--grid-2col">
                      <InputField
                        name="addressOfFirm"
                        placeholder="Address Line 1"
                        value={work.addressOfFirm}
                        error={
                          showErrors &&
                          !!errors.workHistory?.[index]?.addressOfFirm
                        }
                        helperText={
                          showErrors
                            ? errors.workHistory?.[index]?.addressOfFirm
                            : ""
                        }
                        onChange={(e) => handleChange(index, e)}
                      />
                      <InputField
                        name="addressOfFirm"
                        placeholder="Address Line 2"
                        value={work.addressOfFirm}
                        error={false}
                        helperText=""
                        onChange={(e) => handleChange(index, e)}
                      />
                      <InputField
                        name="firmPostcode"
                        placeholder="Postcode"
                        value={work.firmPostcode}
                        error={
                          showErrors &&
                          !!errors.workHistory?.[index]?.firmPostcode
                        }
                        helperText={
                          showErrors
                            ? errors.workHistory?.[index]?.firmPostcode
                            : ""
                        }
                        onChange={(e) => handleChange(index, e)}
                      />
                      <InputField
                        name="firmTown"
                        placeholder="Town/City"
                        value={work.firmTown}
                        error={
                          showErrors && !!errors.workHistory?.[index]?.firmTown
                        }
                        helperText={
                          showErrors
                            ? errors.workHistory?.[index]?.firmTown
                            : ""
                        }
                        onChange={(e) => handleChange(index, e)}
                      />
                      <InputField
                        name="firmCounty"
                        placeholder="County"
                        value={work.firmCounty}
                        error={
                          showErrors &&
                          !!errors.workHistory?.[index]?.firmCounty
                        }
                        helperText={
                          showErrors
                            ? errors.workHistory?.[index]?.firmCounty
                            : ""
                        }
                        onChange={(e) => handleChange(index, e)}
                      />
                    </div>
                  </div>

                  <div className="separator" />

                  <div className="input-group-row">
                    <div className="Content">
                      Contact Details of the Firm
                      <span style={{ color: "var(--Error)" }}>*</span>
                    </div>
                    <div className="input-group">
                      <MobileNumberInput
                        id={`officeTelephone-${index}`}
                        label="Office Telephone"
                        value={work.officeTelephone}
                        onChange={(phone) =>
                          handleChange(index, {
                            target: {
                              name: "officeTelephone",
                              value: phone || "",
                            },
                          })
                        }
                        helpText="This field is required"
                        // isRequired
                        error={
                          (showErrors &&
                            errors.workHistory?.[index]?.officeTelephone) ||
                          ""
                        }
                      />

                      <InputField
                        label={"Email Address"}
                        name="emailAddress"
                        placeholder="Email Address"
                        value={work.emailAddress}
                        error={
                          showErrors &&
                          errors.workHistory?.[index]?.emailAddress
                        }
                        onChange={(e) => handleChange(index, e)}
                        helperText={
                          showErrors
                            ? errors.workHistory?.[index]?.emailAddress
                            : ""
                        }
                      />
                    </div>
                  </div>

                  <div className="input-group-row">
                    {/* <label>{index === 0 ? "Current Income (£)" : "Income (£)"}</label> */}
                    <div className="Content">
                      Approximate Income (£){" "}
                      <span style={{ color: "var(--Error)" }}>*</span>
                    </div>
                    <InputField
                      name="currentIncome"
                      type="text"
                      placeholder=""
                      value={formatWithCommas(work.currentIncome)}
                      error={
                        showErrors && errors.workHistory?.[index]?.currentIncome
                      }
                      onChange={(e) => handleChange(index, e)}
                      helperText={
                        showErrors
                          ? errors.workHistory?.[index]?.currentIncome
                          : ""
                      }
                    />
                  </div>

                  <div className="input-group-row">
                    <div className="Content">
                      Is the firm regulated by the FCA?{" "}
                      <span style={{ color: "var(--Error)" }}>*</span>
                    </div>
                    <div className="input-group">
                      <YesNoRadio
                        name={`FCARegulated-${index}`}
                        value={work.FCARegulated}
                        onChange={(value) => handleChange(index, value)}
                        error={
                          showErrors &&
                          !!errors.workHistory?.[index]?.FCARegulated
                        }
                      />
                    </div>
                  </div>

                  {work.FCARegulated === true && (
                    <>
                      <div className="input-group-row">
                        <div className="Content">
                          Are you or the firm an appointed representative?{" "}
                          <span style={{ color: "var(--Error)" }}>*</span>
                        </div>
                        <div className="input-group">
                          <YesNoRadio
                            name={`appointedRepresentative-${index}`}
                            value={work.appointedRepresentative}
                            error={
                              showErrors &&
                              !!errors.workHistory?.[index]
                                ?.appointedRepresentative
                            }
                            onChange={(value) => handleChange(index, value)}
                          />
                        </div>
                      </div>

                      <div className="input-group-row">
                        <div className="Content">
                          If yes, please confirm the life assurance company or
                          network
                        </div>
                        <InputField
                          name="lifeAssuranceCompany"
                          placeholder=""
                          value={work.lifeAssuranceCompany}
                          error={
                            work.appointedRepresentative === true &&
                            !work.lifeAssuranceCompany
                              ? errors.workHistory?.[index]
                                  ?.lifeAssuranceCompany
                              : errors.workHistory?.[index]
                                  ?.appointedRepresentative
                          }
                          onChange={(e) => handleChange(index, e)}
                        />
                      </div>

                      <div className="input-group-row">
                        <div className="Content">
                          Date your authorisation commenced
                          <span style={{ color: "var(--Error)" }}>*</span>
                        </div>
                        <DateField
                          name={`authorisationCommenced-${index}`}
                          // label="Date"
                          value={work.authorisationCommenced}
                          onChange={(e) => {
                            const dateValue = e.target.value
                              ? new Date(e.target.value)
                              : "";
                            handleDateChange(
                              index,
                              "authorisationCommenced",
                              dateValue,
                            );
                          }}
                          error={
                            showErrors &&
                            !!errors.workHistory?.[index]
                              ?.authorisationCommenced
                          }
                          helperText={
                            showErrors
                              ? errors.workHistory?.[index]
                                  ?.authorisationCommenced
                              : ""
                          }
                        />
                      </div>
                    </>
                  )}

                  <div className="input-group-row">
                    <div className="Content">
                      What is your current job title?
                      <span style={{ color: "var(--Error)" }}>*</span>
                    </div>
                    <InputField
                      name="currentJobTitle"
                      placeholder="Job Title"
                      value={work.currentJobTitle}
                      error={
                        showErrors &&
                        errors.workHistory?.[index]?.currentJobTitle
                      }
                      onChange={(e) => handleChange(index, e)}
                      helperText={
                        showErrors
                          ? errors.workHistory?.[index]?.currentJobTitle
                          : ""
                      }
                    />
                  </div>
                  <div className="input-group-row">
                    <div className="Content">
                      What are your current roles and responsibilities?
                      <span style={{ color: "var(--Error)" }}>*</span>
                    </div>
                    <InputField
                      name="currentRole"
                      placeholder="Roles and Responsibilities"
                      value={work.currentRole}
                      error={
                        showErrors && errors.workHistory?.[index]?.currentRole
                      }
                      onChange={(e) => handleChange(index, e)}
                      helperText={
                        showErrors
                          ? errors.workHistory?.[index]?.currentRole
                          : ""
                      }
                    />
                  </div>

                  <div className="input-group-row">
                    <div className="Content">
                      Is the role regulated?{" "}
                      <span style={{ color: "var(--Error)" }}>*</span>
                    </div>
                    <YesNoRadio
                      name={`regulatedRole-${index}`}
                      value={work.regulatedRole}
                      error={
                        showErrors && errors.workHistory?.[index]?.regulatedRole
                      }
                      onChange={(value) => handleChange(index, value)}
                    />
                  </div>

                  {index === 0 && (
                    <div className="section2-container">
                      <div className="input-group-row">
                        <div className="Content">
                          If yes, what is your notice Period (weeks)
                          <span style={{ color: "var(--Error)" }}>*</span>
                        </div>
                        <InputField
                          name="noticePeriod"
                          type="number"
                          min={0}
                          placeholder="Notice Period"
                          error={
                            showErrors &&
                            !!errors.workHistory?.[index]?.noticePeriod
                          }
                          helperText={
                            showErrors
                              ? errors.workHistory?.[index]?.noticePeriod
                              : ""
                          }
                          value={work.noticePeriod}
                          onChange={(e) => handleChange(index, e)}
                        />
                      </div>
                    </div>
                  )}

                  <div className="input-group-row">
                    <div className="Content">
                      Reasons for leaving{" "}
                      <span style={{ color: "var(--Error)" }}>*</span>
                    </div>
                    <div className="input-group">
                      <RadioButtonGrid
                        options={reasonsForLeaving}
                        columns={2}
                        name={`reasonsForLeaving-${index}`}
                        value={work.reasonsForLeaving}
                        error={errors.workHistory?.[index]?.reasonsForLeaving}
                        onChange={(e) => handleChange(index, e)}
                      />
                    </div>
                  </div>
                  {index === 0 && (
                    <div className="section2-container">
                      <div className="input-group-row">
                        <div className="Content">
                          Please advise when you intend to resign
                          <span style={{ color: "var(--Error)" }}>*</span>
                        </div>
                        <div className="input-group">
                          <DateField
                            name={`intendedResignation-${index}`}
                            // label="Date"
                            value={work.intendedResignation}
                            onChange={(e) => {
                              const dateValue = e.target.value
                                ? new Date(e.target.value)
                                : "";
                              handleDateChange(
                                index,
                                "intendedResignation",
                                dateValue,
                              );
                            }}
                            error={
                              showErrors &&
                              !!errors.workHistory?.[index]?.intendedResignation
                            }
                            helperText={
                              showErrors
                                ? errors.workHistory?.[index]
                                    ?.intendedResignation
                                : ""
                            }
                          />
                        </div>
                      </div>

                      <div className="input-group-row">
                        <div className="Content">
                          Do you have the status of Competent Adviser?
                          <span style={{ color: "var(--Error)" }}>*</span>
                        </div>
                        <div className="input-group">
                          <YesNoRadio
                            name={`competentAdviser-${index}`}
                            value={work.competentAdviser}
                            error={
                              errors.workHistory?.[index]?.competentAdviser
                            }
                            onChange={(value) => handleChange(index, value)}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="input-group-row">
                    <div className="Content">
                      Can we contact this firm for a reference?
                      <span style={{ color: "var(--Error)" }}>*</span>
                    </div>
                    <div className="input-group">
                      <YesNoRadio
                        name={`contactForReference-${index}`}
                        value={work.contactForReference}
                        error={errors.workHistory?.[index]?.contactForReference}
                        onChange={(value) => handleChange(index, value)}
                      />
                    </div>
                  </div>

                  <div className="input-group-row">
                    <div className="Content">
                      If ‘No’, please tell us why
                      {/* <span style={{ color: "var(--Error)" }}>*</span> */}
                    </div>
                    <TextArea
                      name="reasonForNoReference"
                      placeholder="Reason for No Reference"
                      isTextArea
                      value={work.reasonForNoReference}
                      error={
                        work.contactForReference === false &&
                        (work.reasonForNoReference || "").trim() === ""
                          ? "This field is required"
                          : false
                      }
                      helperText={
                        showErrors &&
                        work.contactForReference === false &&
                        (work.reasonForNoReference || "").trim() === ""
                          ? "This field is required"
                          : ""
                      }
                      showError={showErrors}
                      onChange={(e) => handleChange(index, e)}
                      multiline
                      rows={4}
                    />
                  </div>
                </>
              )}
            </div>
            <div className="separator" />
          </React.Fragment>
        );
      })}

      {/* <div className="button-group">
        <button
          type="button"
          className="icon-button delete-button"
          onClick={deleteWorkHistory}
          disabled={isDeletePreviousAddressDisabled}
        >
          <i className="fas fa-trash"></i>
        </button>
      </div> */}
      {canDeleteWorkHistory && (
        <div className="button-group">
          <button
            type="button"
            className="icon-button delete-button"
            onClick={deleteWorkHistory}
            title="Delete last work history row"
          >
            <i className="fas fa-trash"></i>
          </button>
        </div>
      )}

      <div className="input-group-row">
        <div className="Content">
          Please provide dates and names of all the Life Assurance Companies or
          Networks of which you have been an appointed representative in the
          last 5 years
        </div>
        <div className="input-group">
          <div>
            {[...Array(5)].map((_, index) => (
              <React.Fragment key={index}>
                <div
                  className="input-group input-group--grid-2col"
                  style={{ marginBottom: "1rem" }}
                >
                  <DateField
                    name={`date-${index}`}
                    // label="Date"
                    value={formatDateValue(
                      formData.lifeAssuranceCompanies?.[index]?.date,
                    )}
                    onChange={(e) => {
                      const dateValue = e.target.value
                        ? new Date(e.target.value)
                        : "";
                      handleLifeAssuranceChange(index, dateValue);
                    }}
                  />
                  <InputField
                    name={`company-${index}`}
                    placeholder="Company"
                    value={
                      formData.lifeAssuranceCompanies?.[index]?.company || ""
                    }
                    onChange={(e) => handleLifeAssuranceChange(index, e)}
                  />
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
      {!isSubmitted && <PrimaryButton label="SAVE" onClick={handleSubmit} />}
    </form>
  );
};

export default Section3;
